import React from 'react';

/**
 * ONE RENDERER FOR EVERY AUTHORITY REFUSAL.
 *
 * AUTHORITY-CONTAINMENT-1 added no new status codes — its project-node and
 * activity scope refusals arrive as 400 {code, message}, and its award
 * concurrency close arrives as the 403 `capability_required` that already
 * existed. So there is nothing new to render, and building a bespoke panel per
 * defect would have created a second vocabulary for the same answers.
 *
 * What this must never do:
 *
 *   - Infer anything from the server's prose. The `kind` comes from the status
 *     and code; the server's sentence is displayed BESIDE ours, never parsed.
 *   - Turn `reference_out_of_scope` into "not found". The backend answers
 *     identically for "another tenant's" and "does not exist" precisely so a
 *     caller cannot tell which; rendering it as absence rebuilds the existence
 *     oracle the refusal exists to prevent.
 *   - Offer retry on a protective refusal. `retryAllowed` is decided by the
 *     presenter from the backend's answer, and a 409 that protected records
 *     cannot be retried into success.
 *   - Announce itself. This used to carry `role="status" aria-live="polite"`,
 *     which reads as an announcement and is not one: the node is created in
 *     the same DOM commit as its own text, so there is no CHANGE for an
 *     assistive technology to report, and the refusal was silent. Worse, a
 *     browser that did announce it would say it twice once a persistent
 *     announcer existed. Announcing belongs to whatever mounts this panel;
 *     see AuthorityAnnouncer. A refusal is a labelled group, not a live
 *     region.
 */
export const AuthorityRefusal = ({ refusal }) => {
  if (!refusal) return null;
  const protective = refusal.kind === 'withheld';
  return (
    <div
      data-testid="authority-refusal"
      data-kind={refusal.kind}
      data-code={refusal.code || ''}
      data-status={refusal.status ?? ''}
      role="group"
      aria-label={refusal.heading}
      className={
        protective
          ? 'rounded-md border border-sky-300 bg-sky-50 p-3 space-y-2'
          : 'rounded-md border border-slate-300 bg-slate-50 p-3 space-y-2'
      }
    >
      <p className="text-sm font-semibold text-slate-900">{refusal.heading}</p>
      <p className="text-sm text-slate-700">{refusal.lead}</p>

      {refusal.protectedRecords?.length > 0 && (
        <div data-testid="protected-records" className="space-y-1">
          <p className="text-xs font-medium text-slate-700">What was protected</p>
          <ul className="text-xs text-slate-700 list-disc pl-4">
            {/* `label` ALREADY carries the count -- presentProtectedRecords
                returns `${count} ${noun}`. Rendering the count beside it
                printed "3 3 activity instances". The count stays available on
                the object for callers that need it numerically. */}
            {refusal.protectedRecords.map((held) => (
              <li key={held.kind}>{held.label}</li>
            ))}
          </ul>
        </div>
      )}

      {refusal.closing && <p className="text-sm font-medium text-slate-900">{refusal.closing}</p>}

      {/* The server's own sentence, verbatim. Ours says what KIND of answer
          this is; the server's says what it was about. */}
      {refusal.message && (
        <p data-testid="refusal-server-message" className="text-xs text-slate-600">
          {refusal.message}
        </p>
      )}
    </div>
  );
};

export default AuthorityRefusal;
